import { Box, Checkbox, IconButton, Stack } from "@mui/material";
import { Edit } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as Delete } from "../../../assets/Images/Delete.svg";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import useDebounce from "../../Hooks/useDebounce";
import { PAGE_SIZE_50 } from "../CommonComponents/Common";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import DeletePopup from "../CommonComponents/PopUpCards/DeletePopUp";
import ErrorPopUp from "../CommonComponents/PopUpCards/ErrorPopUp";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import AddContactsBody from "./AddContactBody";
import DncHeader from "./DncHeader";
import DncPopover from "./DncPopover";
import ExcelBody from "./ExcelBody";
import {
  createDNCAction,
  deleteDNCAction,
  editDNCAction,
  getDNCListAction,
  saveDNCListExcelAction,
} from "./Store/dnc.action";

const SYSTEM_FIELDS = [
  "_id",
  "phone",
  "origin",
  "params",
  "createdAt",
  "updatedAt",
  "__v",
];

const extractCustomFields = (contact: Record<string, any>) => {
  return Object.entries(contact)
    .filter(([key]) => !SYSTEM_FIELDS.includes(key))
    .map(([key, value]) => ({ key, value }));
};

const normalizePhone = (phone: string) => {
  if (!phone) return phone;
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const DncList = () => {
  const dispatch = useDispatch();
  const reducer: any = useSelector((state) => state);
  const {
    DNCReducer: { dncList: contactsLists, dncListCount },
  } = reducer;
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]) as any;
  const [contact, setContact] = useState("");
  const [contacts, setContacts] = useState([]) as any;
  const [addContactModal, setAddContactModal] = useState(false);
  const [addExcelModal, setAddExcelModal] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [spin, setSpin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addBtnLoading, setAddBtnLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [editContactId, setEditContactId] = useState<string | null>(null);
  const [contactsList, setContactsList] = useState<any[]>([]);
  const [warning, setWarning] = useState({
    show: false,
    warningMsg: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    show: false,
    msg: "",
    heading: "",
  });
  const [searchText, setSearchText] = useState("");
  const debouncedSearchTerm = useDebounce(searchText, 2000);

  useEffect(() => {
    const contacts = contactsLists.map((contact) => ({
      ...contact,
      customFields: extractCustomFields(contact),
    }));
    setContactsList(contacts);
  }, [contactsLists]);

  useEffect(() => {
    fetchDNCList(pageIndex);
  }, [pageIndex, debouncedSearchTerm]);

  useEffect(() => {
    if (pageIndex !== 1) setPageIndex(1);
  }, [debouncedSearchTerm]);

  const fetchDNCList = async (page = pageIndex) => {
    setLoading(true);
    const params = {
      pageSize: PAGE_SIZE_50,
      pageIndex: page,
      searchText: debouncedSearchTerm,
    };
    await dispatch(getDNCListAction(params));
    await dispatch(getDNCListAction({ ...params, count: "true" }));
    setLoading(false);
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(contactsList?.map((ct) => ct?._id));
    } else {
      setSelectedIds([]);
    }
  };

  const confirmDelete = async () => {
    setSpin(true);
    let res;
    if (selectedId) {
      res = await dispatch(deleteDNCAction([selectedId]));
    } else {
      res = await dispatch(deleteDNCAction(selectedIds));
    }
    if (res === 200) {
      setShowDeletePopup(false);
      setSuccessPopup({
        show: true,
        msg: "Contact deleted successfully",
        heading: "Contact Deleted",
      });
    }
    setSelectedIds([]);
    setSelectedId(null);
    setShowDeletePopup(false);
    setSpin(false);
    fetchDNCList(1);
    setPageIndex(1);
  };

  const uploadFile = async () => {
    if (!selectedFile) return;
    setSpin(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("origin", "excel-frontend");
    const res: any = await dispatch(saveDNCListExcelAction(formData));
    if (res === true) {
      setSuccessPopup({
        show: true,
        msg: "File Uploaded Successfully",
        heading: "File Uploaded",
      });
    }
    setSelectedFile(null);
    setAddExcelModal(false);
    setSearchText("");
    fetchDNCList(1);
    setPageIndex(1);
    setSpin(false);
  };

  const handleSaveContacts = async () => {
    setAddBtnLoading(true);
    if (editMode && editContactId) {
      const contactToUpdate: any = contacts[0] || {
        phone: contact,
        customFields: [],
      };
      if (contactToUpdate?.phone?.length < 11) {
        setAddBtnLoading(false);
        setWarning({
          show: true,
          warningMsg: "Phone Number cannot be less than 10 numbers.",
        });
        return;
      }
      if (contactToUpdate?.phone?.length > 16) {
        setAddBtnLoading(false);
        setWarning({
          show: true,
          warningMsg: "Phone Number cannot exceed 15 numbers.",
        });
        return;
      }

      const payload = {
        phone: normalizePhone(contactToUpdate.phone),
        origin: "FrontEnd",
        ...contactToUpdate.customFields.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {}),
      };

      const res: any = await dispatch(editDNCAction(payload, editContactId));

      if (res === 200) {
        setSuccessPopup({
          show: true,
          msg: "Contact updated successfully",
          heading: "Contact Updated",
        });
      }
    } else {
      for (const c of contacts) {
        if (!c.phone?.trim()) {
          setAddBtnLoading(false);
          setWarning({
            show: true,
            warningMsg: "Phone Number cannot be empty.",
          });
          return;
        }

        if (c.phone.length < 11) {
          setAddBtnLoading(false);
          setWarning({
            show: true,
            warningMsg: "Phone Number cannot be less than 10 numbers.",
          });
          return;
        }

        if (c.phone.length > 16) {
          setAddBtnLoading(false);
          setWarning({
            show: true,
            warningMsg: "Phone Number cannot exceed 15 numbers.",
          });
          return;
        }
      }

      const updatedContacts = contacts.map((contactData: any) => ({
        phone: normalizePhone(contactData.phone),
        origin: "FrontEnd",
        ...contactData.customFields.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {}),
      }));

      const res: any = await dispatch(createDNCAction(updatedContacts));
      const ifMulti = contacts?.length > 1;
      if (res === 200) {
        setSuccessPopup({
          show: true,
          msg: ` ${ifMulti ? "Contacts" : "Contact"} added successfully`,
          heading: ` ${ifMulti ? "Contacts" : "Contact"} added`,
        });
      }
    }

    setAddContactModal(false);
    setContacts([]);
    setContact("");
    setEditMode(false);
    setEditContactId(null);
    fetchDNCList(1);
    setPageIndex(1);
    setAddBtnLoading(false);
  };

  return (
    <Box>
      <DncHeader
        setAddContactModal={setAddContactModal}
        setAddExcelModal={setAddExcelModal}
        setShowDeletePopup={setShowDeletePopup}
        selectedIds={selectedIds}
        searchText={searchText}
        setSearchText={setSearchText}
      />

      <Box>
        <DynamicTable
          columns={[
            {
              label: (
                <Checkbox
                  size="small"
                  checked={
                    contactsList.length > 0 &&
                    selectedIds.length === contactsList.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              ),
              render: (row) => (
                <Checkbox
                  size="small"
                  checked={selectedIds.includes(row._id)}
                  onChange={() => handleSelect(row._id)}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              ),
            },
            {
              label: "Contact",
              field: "phone",
            },
            {
              label: "Origin",
              field: "origin",
            },
            {
              label: "Actions",
              render: (row) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <IconButton
                    title="Delete Contact"
                    size="small"
                    onClick={() => {
                      setSelectedId(row?._id);
                      setShowDeletePopup(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                  <IconButton
                    title="Edit Contact"
                    size="small"
                    onClick={() => {
                      setEditMode(true);
                      setEditContactId(row._id);
                      setContact(row.phone);
                      setAddContactModal(true);
                      setContacts([
                        {
                          phone: row.phone,
                          showCustomFields: row.customFields?.length > 0,
                          customFields: row.customFields || [],
                        },
                      ]);
                    }}
                  >
                    <Edit size={18} strokeWidth={1.5} />
                  </IconButton>
                  <DncPopover dncData={row} />
                </Stack>
              ),
            },
          ]}
          loading={loading}
          data={contactsList}
          noDataAvailableMessage="No Contacts Found"
          currentPage={pageIndex}
          handlePageChange={(event, page: any) => setPageIndex(page)}
          handlePrevious={() => setPageIndex(pageIndex - 1)}
          handleNext={() => setPageIndex(pageIndex + 1)}
          totalPages={Math.ceil(dncListCount / PAGE_SIZE_50)}
          rowCount={contactsList?.length}
          totalRowCount={dncListCount}
        />
      </Box>
      {addContactModal && (
        <CommonModal
          body={
            <AddContactsBody
              contacts={contacts}
              setContacts={setContacts}
              contact={contact}
              setContact={setContact}
              editMode={editMode}
            />
          }
          handleSave={handleSaveContacts}
          heading={editMode ? "Edit Contact" : "Add Contacts"}
          show={addContactModal}
          size={600}
          toggle={() => {
            setContacts([]);
            setContact("");
            setEditMode(false);
            setEditContactId(null);
            setAddContactModal((prev) => !prev);
          }}
          saveButtonText={editMode ? "Update" : "Add"}
          spin={addBtnLoading}
          disableSaveButton={editMode ? !contact : contacts?.length === 0}
        />
      )}

      {addExcelModal && (
        <CommonModal
          body={
            <ExcelBody
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          }
          handleSave={uploadFile}
          heading={`Upload Excel`}
          show={addExcelModal}
          size={600}
          toggle={() => setAddExcelModal((prev) => !prev)}
          saveButtonText="Upload"
          spin={spin}
          disableSaveButton={!selectedFile}
        />
      )}
      {showDeletePopup && (
        <DeletePopup
          show={showDeletePopup}
          toggle={() => setShowDeletePopup(false)}
          message="Are you sure you want to delete the selected contact(s)?"
          handleDelete={confirmDelete}
          spin={spin}
          heading="Delete Contact"
        />
      )}
      {warning?.show && (
        <ErrorPopUp
          show={warning?.show}
          headerText="Warning"
          toggle={() =>
            setWarning((prev) => ({
              ...prev,
              show: false,
              warningMsg: "",
            }))
          }
          heading={"Warning"}
          message={warning?.warningMsg}
          spin={false}
          Icon={ConfirmationIcon}
        />
      )}
      {successPopup.show && (
        <CommonPopup
          message={successPopup.msg}
          show={successPopup.show}
          toggle={() =>
            setSuccessPopup((prev) => ({
              ...prev,
              show: false,
              msg: "",
              heading: "",
            }))
          }
          Icon={SuccessIcon}
          heading={successPopup.heading}
        />
      )}
    </Box>
  );
};

export default DncList;
